import React, { useEffect, useState } from 'react';
import { db } from '../../../../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy, getDoc } from 'firebase/firestore';
import { Plus, Trash2, Edit2, Image as ImageIcon, Check, X, Search, GraduationCap, FileSpreadsheet, Upload, Download, BarChart2 } from 'lucide-react';
import AdminPageHeader from '../../frame/RightPanelHeader';
import { SiteSettingModel, SUBJECT_TYPES, GRADE_TYPES, TERM_TYPES, UNIVERSITIES } from '../../../../models';
import { LaurelSuccessCard } from '../../../../components/ui/LaurelSuccessCard';
import { SuccessAnalyticsChart } from '../../../../components/ui/SuccessAnalyticsChart';
import { SuccessStoryExportPoster } from '../../../../components/ui/SuccessStoryExportPoster';
import * as XLSX from 'xlsx';

interface SuccessStory {
    id?: string;
    title: string;
    studentName: string;
    school: string;
    department: string;
    imageUrl: string;
    content: string;
    createdAt: number;
    type?: 'university' | 'high_school' | 'grade' | 'rank' | 'standing' | 'improvement';
    subject?: string;
    score?: string;
    grade?: string;
    admissionSchool?: string;
    rank?: string;
    standing?: string;
    improvementScore?: string;
    year?: string;
    semester?: string;
}

const getAchievementValue = (s: any) => {
    if (s.type === 'grade') return (s.score || '').toString().trim();
    if (s.type === 'rank') return (s.rank || '').toString().trim();
    if (s.type === 'standing') return (s.standing || '').toString().trim();
    if (s.type === 'improvement') return (s.improvementScore || '').toString().trim();
    if (s.type === 'high_school' || s.type === 'university') return (s.admissionSchool || '').toString().trim();
    return (s.score || s.rank || s.standing || s.improvementScore || s.admissionSchool || '').toString().trim();
};

const isDuplicateStory = (a: any, b: any) => {
    const yearA = (a.year || '').toString().trim();
    const yearB = (b.year || '').toString().trim();

    const semA = (a.semester || '').toString().trim();
    const semB = (b.semester || '').toString().trim();

    const subjA = (a.subject || '').toString().trim();
    const subjB = (b.subject || '').toString().trim();

    const nameA = (a.studentName || '').toString().trim();
    const nameB = (b.studentName || '').toString().trim();

    const valA = getAchievementValue(a);
    const valB = getAchievementValue(b);

    const typeA = a.type || 'grade';
    const typeB = b.type || 'grade';

    if (typeA !== typeB) return false;
    if (nameA !== nameB) return false;
    if (yearA !== yearB) return false;
    if (semA !== semB) return false;
    if (valA !== valB) return false;

    if (typeA === 'grade' || typeA === 'rank' || typeA === 'standing' || typeA === 'improvement') {
        if (subjA !== subjB) return false;
    }

    return true;
};

const SuccessRoot: React.FC = () => {
    const [stories, setStories] = useState<SuccessStory[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [universities, setUniversities] = useState<{ name: string; logoUrl: string }[]>([]);
    const [selectedYear, setSelectedYear] = useState<string>('전체');
    const [selectedSemester, setSelectedSemester] = useState<string>('전체');
    const [selectedSubject, setSelectedSubject] = useState<string>('전체');
    const [visibleCount, setVisibleCount] = useState(12);
    const [activeSubTab, setActiveSubTab] = useState<'grade' | 'improvement' | 'rank' | 'standing' | 'high_school' | 'university' | 'analytics'>('grade');

    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const observerTargetRef = React.useRef<HTMLDivElement>(null);

    const [form, setForm] = useState<SuccessStory>({
        title: '',
        studentName: '',
        school: '',
        department: '',
        imageUrl: '',
        content: '',
        createdAt: Date.now(),
        type: 'grade',
        subject: SUBJECT_TYPES[0],
        score: '',
        grade: '고등1',
        admissionSchool: '',
        rank: '',
        standing: '',
        improvementScore: '',
        year: new Date().getFullYear().toString(),
        semester: TERM_TYPES[0]
    });

    useEffect(() => {
        fetchStories();
        fetchSettings();
    }, []);

    const fetchStories = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'successStories'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SuccessStory));
            setStories(data);
        } catch (error) {
            console.error('Error fetching stories:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const docRef = doc(db, 'settings', 'site');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data() as SiteSettingModel;
                if (data.universities && data.universities.length > 0) {
                    setUniversities(data.universities);
                } else {
                    setUniversities(UNIVERSITIES as any);
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    const handleAddClick = () => {
        setForm({
            title: '',
            studentName: '',
            school: '',
            department: '',
            imageUrl: '',
            content: '',
            createdAt: Date.now(),
            type: activeSubTab,
            subject: (activeSubTab === 'grade' || activeSubTab === 'rank' || activeSubTab === 'standing' || activeSubTab === 'improvement') ? SUBJECT_TYPES[0] : '',
            score: '',
            grade: '고등1',
            admissionSchool: '',
            rank: '',
            standing: '',
            improvementScore: '',
            year: new Date().getFullYear().toString(),
            semester: TERM_TYPES[0]
        });
        setEditingId(null);
        setIsEditing(true);
    };

    const handleDownloadTemplate = () => {
        const sampleData = [
            { '학교': '매탄중학교', '학년': '중등2', '이름': '정0람', '과목': '수학', '점수': '100', '연도': '2026', '학기': '1학기 기말고사', '타입': '성적' },
            { '학교': '매탄중학교', '학년': '중등2', '이름': '김0원', '과목': '수학', '점수': '1', '연도': '2026', '학기': '1학기 기말고사', '타입': '등급' },
            { '학교': '매탄중학교', '학년': '중등2', '이름': '이0준', '과목': '수학', '점수': '전교 1등', '연도': '2026', '학기': '1학기 기말고사', '타입': '등수' },
            { '학교': '매탄중학교', '학년': '중등2', '이름': '박0욱', '과목': '수학', '점수': '20점 향상', '연도': '2026', '학기': '1학기 기말고사', '타입': '향상' },
            { '학교': '상산고등학교', '학년': '중등3', '이름': '고0림', '과목': '', '점수': '상산고등학교', '연도': '2026', '학기': '', '타입': '고입' },
            { '학교': '대치고등학교', '학년': '고등3', '이름': '김0준', '과목': '', '점수': '서울대학교', '연도': '2026', '학기': '', '타입': '대입' }
        ];
        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '실적등록양식');
        XLSX.writeFile(workbook, '정성우학원_실적업로드_양식.xlsx');
    };

    const handleDownloadExcel = () => {
        const targetList = filteredStories.length > 0 ? filteredStories : stories;
        if (targetList.length === 0) {
            alert('다운로드할 실적 데이터가 없습니다.');
            return;
        }

        const typeToKorean = (t?: string) => {
            if (t === 'grade') return '성적';
            if (t === 'rank') return '등급';
            if (t === 'standing') return '등수';
            if (t === 'improvement') return '향상';
            if (t === 'high_school') return '고입';
            if (t === 'university') return '대입';
            return '성적';
        };

        const excelData = targetList.map(s => {
            let scoreVal = '';
            if (s.type === 'grade') scoreVal = s.score || '';
            else if (s.type === 'rank') scoreVal = s.rank || '';
            else if (s.type === 'standing') scoreVal = s.standing || '';
            else if (s.type === 'improvement') scoreVal = s.improvementScore || '';
            else if (s.type === 'high_school' || s.type === 'university') scoreVal = s.admissionSchool || '';

            return {
                '학교': s.school || '',
                '학년': s.grade || '',
                '이름': s.studentName || '',
                '과목': s.subject || '',
                '점수': scoreVal,
                '연도': s.year || '',
                '학기': s.semester || '',
                '타입': typeToKorean(s.type)
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '실적목록');
        XLSX.writeFile(workbook, `정성우학원_실적목록_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setLoading(true);
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonRows: any[] = XLSX.utils.sheet_to_json(worksheet);

            if (jsonRows.length === 0) {
                alert('엑셀 파일에 데이터가 없습니다.');
                return;
            }

            let successCount = 0;
            let duplicateCount = 0;
            const existingList = [...stories];

            for (const row of jsonRows) {
                const getColVal = (keys: string[]) => {
                    for (const key of keys) {
                        for (const rowKey of Object.keys(row)) {
                            if (rowKey.trim() === key || rowKey.trim().replace(/\s+/g, '') === key.replace(/\s+/g, '')) {
                                return row[rowKey]?.toString().trim() || '';
                            }
                        }
                    }
                    return '';
                };

                const school = getColVal(['학교', '재학학교', '재학중인학교', '재학중인 학교']);
                const grade = getColVal(['학년']);
                const studentName = getColVal(['이름', '학생이름', '학생 명', '학생명']);
                const subject = getColVal(['과목']);
                const val = getColVal(['점수', '수치', '성적', '등급', '등수', '향상점수', '입학학교', '합격학교']);
                const year = getColVal(['연도', '년도']) || new Date().getFullYear().toString();
                const semester = getColVal(['학기', '학기/시험', '시험']) || TERM_TYPES[0];
                const typeStr = getColVal(['타입', '실적타입', '실적구분', '구분', '카테고리']);
                const department = getColVal(['학과', '전공', '전공과목']);

                if (!studentName && !school) continue;

                let type: 'grade' | 'rank' | 'standing' | 'improvement' | 'high_school' | 'university' = 'grade';
                const t = (typeStr || '').toLowerCase();
                if (t.includes('등급') || t === 'rank') type = 'rank';
                else if (t.includes('등수') || t === 'standing') type = 'standing';
                else if (t.includes('향상') || t === 'improvement') type = 'improvement';
                else if (t.includes('고입') || t === 'high_school') type = 'high_school';
                else if (t.includes('대입') || t === 'university') type = 'university';
                else if (t.includes('성적') || t.includes('점수') || t === 'grade') type = 'grade';

                let finalTitle = '';
                let finalDepartment = '';
                let finalContent = '';
                let score = '';
                let rank = '';
                let standing = '';
                let improvementScore = '';
                let admissionSchool = '';

                const examName = `${year ? `${year}년 ` : ''}${semester ? `${semester} ` : ''}`;

                if (type === 'university') {
                    admissionSchool = val;
                    finalTitle = `${studentName} 학생 대입 합격`;
                    finalDepartment = department;
                    finalContent = `${school} ${grade} ${studentName} 학생이 ${admissionSchool}${department ? ` ${department}` : ''}에 최종 합격하였습니다.`;
                } else if (type === 'high_school') {
                    admissionSchool = val;
                    finalTitle = `${studentName} 학생 고입 합격`;
                    finalDepartment = '';
                    finalContent = `${school} ${grade} ${studentName} 학생이 ${admissionSchool}에 최종 합격하였습니다.`;
                } else if (type === 'grade') {
                    score = val;
                    finalTitle = `${studentName} 학생 성적 우수`;
                    const displayScore = score ? (score.includes('점') ? score : `${score}점`) : '';
                    finalDepartment = `${examName}${subject} ${displayScore}`;
                    finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${displayScore}을 달성하였습니다.`;
                } else if (type === 'rank') {
                    rank = val;
                    finalTitle = `${studentName} 학생 등급 우수`;
                    const displayRank = rank ? (rank.includes('등급') ? rank : `${rank}등급`) : '';
                    finalDepartment = `${examName}${subject} ${displayRank}`;
                    finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${displayRank}을 달성하였습니다.`;
                } else if (type === 'standing') {
                    standing = val;
                    finalTitle = `${studentName} 학생 등수 우수`;
                    const displayStanding = standing ? (standing.includes('등') ? standing : `${standing}등`) : '';
                    finalDepartment = `${examName}${subject} ${displayStanding}`;
                    finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${displayStanding}을 달성하였습니다.`;
                } else if (type === 'improvement') {
                    improvementScore = val;
                    finalTitle = `${studentName} 학생 성적 향상`;
                    finalDepartment = `${examName}${subject} ${improvementScore}`;
                    finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${improvementScore} 성적 향상을 이뤄냈습니다.`;
                }

                const payload: any = {
                    title: finalTitle,
                    studentName,
                    school,
                    grade: grade || '고등1',
                    department: finalDepartment,
                    content: finalContent,
                    imageUrl: '',
                    createdAt: Date.now(),
                    type,
                    subject,
                    score,
                    rank,
                    standing,
                    improvementScore,
                    admissionSchool,
                    year,
                    semester
                };

                const isDup = existingList.some(existing => isDuplicateStory(existing, payload));
                if (isDup) {
                    duplicateCount++;
                    continue;
                }

                const docRef = await addDoc(collection(db, 'successStories'), payload);
                existingList.push({ id: docRef.id, ...payload });
                successCount++;
            }

            if (duplicateCount > 0) {
                alert(`엑셀 일괄 등록 완료!\n- 신규 등록: ${successCount}건\n- 중복 제외: ${duplicateCount}건`);
            } else {
                alert(`총 ${successCount}건의 실적이 성공적으로 업로드 및 등록되었습니다!`);
            }
            fetchStories();
        } catch (err) {
            console.error('Error uploading Excel file:', err);
            alert('엑셀 파일 읽기 및 업로드 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleCleanDuplicates = async () => {
        if (!window.confirm('DB 내 중복 등록된 실적 데이터를 검사하여 정리하시겠습니까?')) return;

        try {
            setLoading(true);
            const q = query(collection(db, 'successStories'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const allStories = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SuccessStory));

            const seen: SuccessStory[] = [];
            const toDeleteIds: string[] = [];

            for (const story of allStories) {
                const isDup = seen.some(s => isDuplicateStory(s, story));
                if (isDup) {
                    if (story.id) toDeleteIds.push(story.id);
                } else {
                    seen.push(story);
                }
            }

            if (toDeleteIds.length === 0) {
                alert('중복된 실적 데이터가 없습니다.');
                return;
            }

            for (const id of toDeleteIds) {
                await deleteDoc(doc(db, 'successStories', id));
            }

            alert(`총 ${toDeleteIds.length}건의 중복 데이터를 성공적으로 삭제 정돈했습니다!`);
            fetchStories();
        } catch (error) {
            console.error('Error cleaning duplicates:', error);
            alert('중복 데이터 정리 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let finalTitle = '';
            let finalDepartment = '';
            let finalContent = '';

            const studentName = form.studentName || '';
            const school = form.school || '';
            const grade = form.grade || '';
            const admissionSchool = form.admissionSchool || '';
            const subject = form.subject || '';
            const score = form.score || '';
            const rank = form.rank || '';
            const standing = form.standing || '';
            const improvementScore = form.improvementScore || '';
            const department = form.department || '';
            const year = form.year || '';
            const semester = form.semester || '';

            if (form.type === 'university') {
                finalTitle = `${studentName} 학생 대입 합격`;
                finalDepartment = department;
                finalContent = `${school} ${grade} ${studentName} 학생이 ${admissionSchool}${department ? ` ${department}` : ''}에 최종 합격하였습니다.`;
            } else if (form.type === 'high_school') {
                finalTitle = `${studentName} 학생 고입 합격`;
                finalDepartment = '';
                finalContent = `${school} ${grade} ${studentName} 학생이 ${admissionSchool}에 최종 합격하였습니다.`;
            } else if (form.type === 'grade') {
                finalTitle = `${studentName} 학생 성적 우수`;
                const displayScore = score ? (score.includes('점') ? score : `${score}점`) : '';
                const examName = `${year ? `${year}년 ` : ''}${semester ? `${semester} ` : ''}`;
                finalDepartment = `${examName}${subject} ${displayScore}`;
                finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${displayScore}을 달성하였습니다.`;
            } else if (form.type === 'rank') {
                finalTitle = `${studentName} 학생 등급 우수`;
                const displayRank = rank ? (rank.includes('등급') ? rank : `${rank}등급`) : '';
                const examName = `${year ? `${year}년 ` : ''}${semester ? `${semester} ` : ''}`;
                finalDepartment = `${examName}${subject} ${displayRank}`;
                finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${displayRank}을 달성하였습니다.`;
            } else if (form.type === 'standing') {
                finalTitle = `${studentName} 학생 등수 우수`;
                const displayStanding = standing ? (standing.includes('등') ? standing : `${standing}등`) : '';
                const examName = `${year ? `${year}년 ` : ''}${semester ? `${semester} ` : ''}`;
                finalDepartment = `${examName}${subject} ${displayStanding}`;
                finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${displayStanding}을 달성하였습니다.`;
            } else if (form.type === 'improvement') {
                finalTitle = `${studentName} 학생 성적 향상`;
                const examName = `${year ? `${year}년 ` : ''}${semester ? `${semester} ` : ''}`;
                finalDepartment = `${examName}${subject} ${improvementScore}`;
                finalContent = `${school} ${grade} ${studentName} 학생이 ${examName}시험에서 ${subject} 과목 ${improvementScore} 성적 향상을 이뤄냈습니다.`;
            }

            const payload = {
                ...form,
                title: finalTitle,
                department: finalDepartment,
                content: finalContent,
                imageUrl: ''
            };

            const isDup = stories.some(s => s.id !== editingId && isDuplicateStory(s, payload));
            if (isDup) {
                alert(`[중복 등록 방지]\n${payload.studentName} 학생의 동일한 실적 데이터(${payload.year}년 ${payload.semester} ${payload.subject || ''} ${getAchievementValue(payload)})가 이미 등록되어 있습니다.`);
                return;
            }

            if (editingId) {
                await updateDoc(doc(db, 'successStories', editingId), payload);
            } else {
                await addDoc(collection(db, 'successStories'), { ...payload, createdAt: Date.now() });
            }
            setForm({
                title: '',
                studentName: '',
                school: '',
                department: '',
                imageUrl: '',
                content: '',
                createdAt: Date.now(),
                type: activeSubTab,
                subject: (activeSubTab === 'grade' || activeSubTab === 'rank' || activeSubTab === 'standing' || activeSubTab === 'improvement') ? SUBJECT_TYPES[0] : '',
                score: '',
                grade: '고등1',
                admissionSchool: '',
                rank: '',
                standing: '',
                improvementScore: '',
                year: new Date().getFullYear().toString(),
                semester: TERM_TYPES[0]
            });
            setIsEditing(false);
            setEditingId(null);
            fetchStories();
        } catch (error) {
            console.error('Error saving story:', error);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'successStories', id));
            setStories(prev => prev.filter(s => s.id !== id));
        } catch (error) {
            console.error('Error deleting story:', error);
            fetchStories();
        }
    };

    const startEdit = (story: SuccessStory) => {
        setForm({
            ...story,
            type: story.type || 'university',
            subject: story.subject || '',
            score: story.score || '',
            grade: story.grade || '',
            admissionSchool: story.admissionSchool || '',
            rank: story.rank || '',
            standing: story.standing || '',
            improvementScore: story.improvementScore || '',
            year: story.year || new Date().getFullYear().toString(),
            semester: story.semester || TERM_TYPES[0]
        });
        setEditingId(story.id || null);
        setIsEditing(true);
    };

    const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedName = e.target.value;
        const selectedUni = universities.find(u => u.name === selectedName);
        setForm(prev => ({
            ...prev,
            school: selectedName,
            imageUrl: selectedUni?.logoUrl || prev.imageUrl // Use selected logo, or keep existing if none found (or allow manual override later)
        }));
    };

    // Available years extracted dynamically
    const availableYears = Array.from(
        new Set(
            stories
                .map(s => s.year)
                .filter((y): y is string => Boolean(y && y.trim()))
        )
    ).sort((a, b) => Number(b) - Number(a));

    const currentYear = new Date().getFullYear();
    const yearList = availableYears.length > 0
        ? availableYears
        : [currentYear.toString(), (currentYear - 1).toString(), (currentYear - 2).toString()];

    const isYearMatch = (yearInDb?: string, selectedYear?: string) => {
        if (!selectedYear || selectedYear === '전체') return true;
        if (!yearInDb) return false;
        const dbY = yearInDb.toString().replace(/년|\s+/g, '');
        const selY = selectedYear.toString().replace(/년|\s+/g, '');
        return dbY === selY;
    };

    const isSemesterMatch = (semesterInDb?: string, selectedSemester?: string) => {
        if (!selectedSemester || selectedSemester === '전체') return true;
        if (!semesterInDb) return false;
        const dbS = semesterInDb.toString().replace(/고사|\s+/g, '');
        const selS = selectedSemester.toString().replace(/고사|\s+/g, '');
        return dbS === selS || dbS.includes(selS) || selS.includes(dbS);
    };

    const isSubjectMatch = (subjectInDb?: string, selectedSubject?: string) => {
        if (!selectedSubject || selectedSubject === '전체') return true;
        if (!subjectInDb) return false;
        return subjectInDb.trim() === selectedSubject.trim();
    };

    // Filter & Sort stories by highest rank/score first
    const filteredStories = stories
        .filter(s => {
            const matchesTab = (s.type || 'university') === activeSubTab;
            const matchesYear = isYearMatch(s.year, selectedYear);
            const matchesSemester = isSemesterMatch(s.semester, selectedSemester);
            const matchesSubject = isSubjectMatch(s.subject, selectedSubject);
            const matchesSearch =
                (s.studentName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.school || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.admissionSchool || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (s.subject || '').toLowerCase().includes(searchTerm.toLowerCase());
            return matchesTab && matchesYear && matchesSemester && matchesSubject && matchesSearch;
        })
        .sort((a, b) => {
            const type = a.type || 'university';

            if (type === 'grade') {
                // High score first (descending: 100 > 95 > 90)
                const scoreA = parseFloat((a.score || '').replace(/[^0-9.]/g, '')) || 0;
                const scoreB = parseFloat((b.score || '').replace(/[^0-9.]/g, '')) || 0;
                if (scoreB !== scoreA) return scoreB - scoreA;
            } else if (type === 'rank') {
                // Small rank number first (ascending: 1등급 < 2등급 < 3등급)
                const rankA = parseFloat((a.rank || '').replace(/[^0-9.]/g, '')) || 99;
                const rankB = parseFloat((b.rank || '').replace(/[^0-9.]/g, '')) || 99;
                if (rankA !== rankB) return rankA - rankB;
            } else if (type === 'standing') {
                // Small standing number first (ascending: 1등 < 2등 < 3등)
                const standA = parseFloat((a.standing || '').replace(/[^0-9.]/g, '')) || 999;
                const standB = parseFloat((b.standing || '').replace(/[^0-9.]/g, '')) || 999;
                if (standA !== standB) return standA - standB;
            } else if (type === 'improvement') {
                // High improvement first (descending: 30점 향상 > 20점 향상)
                const impA = parseFloat((a.improvementScore || '').replace(/[^0-9.]/g, '')) || 0;
                const impB = parseFloat((b.improvementScore || '').replace(/[^0-9.]/g, '')) || 0;
                if (impB !== impA) return impB - impA;
            }

            // Secondary sort: newest createdAt first
            return (b.createdAt || 0) - (a.createdAt || 0);
        });

    const displayedStories = filteredStories.slice(0, visibleCount);
    const hasMore = visibleCount < filteredStories.length;

    useEffect(() => {
        setVisibleCount(12);
    }, [searchTerm, activeSubTab]);

    useEffect(() => {
        const target = observerTargetRef.current;
        if (!target || !hasMore) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setVisibleCount(prev => prev + 12);
            }
        }, { threshold: 0.1, rootMargin: '100px' });

        observer.observe(target);
        return () => {
            if (target) observer.unobserve(target);
        };
    }, [hasMore, filteredStories]);

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden w-full animate-in fade-in duration-500">
            <AdminPageHeader
                title="실적 관리"
                icon={GraduationCap}
                subtitle="관리자 전용 실적 등록 및 편집 게시판입니다."
            />

            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
                <div className="max-w-6xl mx-auto space-y-6">

                    {/* Dedicated Action Toolbar */}
                    {!isEditing && (
                        <div className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600">
                                    <GraduationCap size={16} />
                                </div>
                                <div>
                                    <h4 className="text-xs font-black text-slate-800">실적 관리 도구함</h4>
                                    <p className="text-[11px] text-slate-400 font-medium">실적 데이터 엑셀 가져오기/내보내기, 중복 정리 및 포스터 캡처</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept=".xlsx, .xls, .csv"
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={handleDownloadTemplate}
                                    className="bg-slate-50 text-slate-700 hover:bg-slate-100 px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border border-slate-200"
                                    title="엑셀 작성 양식 다운로드"
                                >
                                    <FileSpreadsheet size={15} className="text-emerald-600" />
                                    <span>양식 다운로드</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleUploadClick}
                                    className="bg-emerald-600 text-white hover:bg-emerald-700 px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
                                >
                                    <Upload size={15} />
                                    <span>엑셀 업로드</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDownloadExcel}
                                    className="bg-slate-800 text-white hover:bg-slate-900 px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
                                >
                                    <Download size={15} />
                                    <span>엑셀 다운로드</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCleanDuplicates}
                                    className="bg-amber-50 text-amber-700 hover:bg-amber-100 px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border border-amber-200 shadow-sm active:scale-95"
                                    title="DB 내 중복 등록된 실적 데이터 검사 및 일괄 정리"
                                >
                                    <Trash2 size={15} className="text-amber-600" />
                                    <span>중복 정리</span>
                                </button>
                                <SuccessStoryExportPoster
                                    stories={filteredStories}
                                    activeSubTab={activeSubTab}
                                    selectedYear={selectedYear}
                                    selectedSemester={selectedSemester}
                                    selectedSubject={selectedSubject}
                                />
                                <button
                                    onClick={handleAddClick}
                                    className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-black hover:bg-indigo-700 transition-all flex items-center gap-1.5 shadow-md shadow-indigo-100 active:scale-95"
                                >
                                    <Plus size={16} strokeWidth={3} />
                                    <span>새 실적 등록</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {isEditing ? (
                        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-xl space-y-6">
                            <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                                <h2 className="text-xl font-black text-slate-800">{editingId ? '실적 수정' : '새 실적 등록'}</h2>
                                <button onClick={() => setIsEditing(false)} className="text-slate-400 hover:text-slate-600">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-wider">실적 구분</label>
                                    <div className="flex gap-2 p-1 bg-slate-100 rounded-xl w-fit">
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, type: 'grade', subject: form.subject || SUBJECT_TYPES[0] })}
                                            className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${form.type === 'grade'
                                                    ? 'bg-white text-slate-900 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-900'
                                                }`}
                                        >
                                            성적
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, type: 'improvement', subject: form.subject || SUBJECT_TYPES[0] })}
                                            className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${form.type === 'improvement'
                                                    ? 'bg-white text-slate-900 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-900'
                                                }`}
                                        >
                                            향상
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, type: 'rank', subject: form.subject || SUBJECT_TYPES[0] })}
                                            className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${form.type === 'rank'
                                                    ? 'bg-white text-slate-900 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-900'
                                                }`}
                                        >
                                            등급
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, type: 'standing', subject: form.subject || SUBJECT_TYPES[0] })}
                                            className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${form.type === 'standing'
                                                    ? 'bg-white text-slate-900 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-900'
                                                }`}
                                        >
                                            등수
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, type: 'high_school' })}
                                            className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${form.type === 'high_school'
                                                    ? 'bg-white text-slate-900 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-900'
                                                }`}
                                        >
                                            고입
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, type: 'university' })}
                                            className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${form.type === 'university'
                                                    ? 'bg-white text-slate-900 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-900'
                                                }`}
                                        >
                                            대입
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-wider">학생 이름</label>
                                    <input
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                        value={form.studentName}
                                        onChange={e => setForm({ ...form, studentName: e.target.value })}
                                        placeholder="예: 홍길동"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-wider">재학중인 학교</label>
                                    <input
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                        value={form.school}
                                        onChange={e => setForm({ ...form, school: e.target.value })}
                                        placeholder="예: 대치중학교, 상산고등학교"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-wider">학년</label>
                                    <select
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none bg-white font-medium"
                                        value={form.grade || '고등1'}
                                        onChange={e => setForm({ ...form, grade: e.target.value })}
                                    >
                                        {GRADE_TYPES.map((grd) => (
                                            <option key={grd} value={grd}>{grd}</option>
                                        ))}
                                    </select>
                                </div>

                                {(form.type === 'university' || form.type === 'high_school') && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">입학한 학교</label>
                                            <input
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                                value={form.admissionSchool || ''}
                                                onChange={e => setForm({ ...form, admissionSchool: e.target.value })}
                                                placeholder={form.type === 'university' ? "예: 서울대학교" : "예: 상산고등학교"}
                                            />
                                        </div>
                                        {form.type === 'university' && (
                                            <div className="space-y-2">
                                                <label className="text-xs font-black text-slate-500 uppercase tracking-wider">전공과목 (학과)</label>
                                                <input
                                                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                                    value={form.department || ''}
                                                    onChange={e => setForm({ ...form, department: e.target.value })}
                                                    placeholder="예: 경영학과, 의예과"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}

                                {(form.type === 'grade' || form.type === 'rank' || form.type === 'standing' || form.type === 'improvement') && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">연도</label>
                                            <input
                                                required
                                                type="number"
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                                value={form.year || ''}
                                                onChange={e => setForm({ ...form, year: e.target.value })}
                                                placeholder="예: 2026"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">학기/시험</label>
                                            <select
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none bg-white font-medium"
                                                value={form.semester || TERM_TYPES[0]}
                                                onChange={e => setForm({ ...form, semester: e.target.value })}
                                            >
                                                {TERM_TYPES.map((term) => (
                                                    <option key={term} value={term}>{term}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                )}

                                {form.type === 'grade' && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">과목</label>
                                            <select
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none bg-white font-medium"
                                                value={form.subject || SUBJECT_TYPES[0]}
                                                onChange={e => setForm({ ...form, subject: e.target.value })}
                                            >
                                                {SUBJECT_TYPES.map((sub) => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">성적 (점수)</label>
                                            <input
                                                required
                                                type="number"
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                                value={form.score || ''}
                                                onChange={e => setForm({ ...form, score: e.target.value })}
                                                placeholder="예: 100"
                                            />
                                        </div>
                                    </>
                                )}

                                {form.type === 'rank' && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">과목</label>
                                            <select
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none bg-white font-medium"
                                                value={form.subject || SUBJECT_TYPES[0]}
                                                onChange={e => setForm({ ...form, subject: e.target.value })}
                                            >
                                                {SUBJECT_TYPES.map((sub) => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">등급</label>
                                            <input
                                                required
                                                type="number"
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                                value={form.rank || ''}
                                                onChange={e => setForm({ ...form, rank: e.target.value })}
                                                placeholder="예: 1"
                                            />
                                        </div>
                                    </>
                                )}

                                {form.type === 'standing' && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">과목</label>
                                            <select
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none bg-white font-medium"
                                                value={form.subject || SUBJECT_TYPES[0]}
                                                onChange={e => setForm({ ...form, subject: e.target.value })}
                                            >
                                                {SUBJECT_TYPES.map((sub) => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">등수</label>
                                            <input
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                                value={form.standing || ''}
                                                onChange={e => setForm({ ...form, standing: e.target.value })}
                                                placeholder="예: 1등, 전교 1등"
                                            />
                                        </div>
                                    </>
                                )}

                                {form.type === 'improvement' && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">과목</label>
                                            <select
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none bg-white font-medium"
                                                value={form.subject || SUBJECT_TYPES[0]}
                                                onChange={e => setForm({ ...form, subject: e.target.value })}
                                            >
                                                {SUBJECT_TYPES.map((sub) => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-slate-500 uppercase tracking-wider">향상점수</label>
                                            <input
                                                required
                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                                value={form.improvementScore || ''}
                                                onChange={e => setForm({ ...form, improvementScore: e.target.value })}
                                                placeholder="예: 20점 향상, 30점 향상"
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="md:col-span-2 flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsEditing(false)}
                                        className="px-6 py-3 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200"
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-10 py-3 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-100"
                                    >
                                        {editingId ? '수정 완료' : '등록하기'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Tab Bar */}
                            <div className="flex flex-wrap gap-2 p-1 bg-slate-100 rounded-2xl w-fit mb-6">
                                <button
                                    onClick={() => setActiveSubTab('grade')}
                                    className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeSubTab === 'grade'
                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/10'
                                            : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                >
                                    성적
                                </button>
                                <button
                                    onClick={() => setActiveSubTab('improvement')}
                                    className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeSubTab === 'improvement'
                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/10'
                                            : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                >
                                    향상
                                </button>
                                <button
                                    onClick={() => setActiveSubTab('rank')}
                                    className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeSubTab === 'rank'
                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/10'
                                            : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                >
                                    등급
                                </button>
                                <button
                                    onClick={() => setActiveSubTab('standing')}
                                    className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeSubTab === 'standing'
                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/10'
                                            : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                >
                                    등수
                                </button>
                                <button
                                    onClick={() => setActiveSubTab('high_school')}
                                    className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeSubTab === 'high_school'
                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/10'
                                            : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                >
                                    고입
                                </button>
                                <button
                                    onClick={() => setActiveSubTab('university')}
                                    className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeSubTab === 'university'
                                            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/10'
                                            : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                >
                                    대입
                                </button>
                                <button
                                    onClick={() => setActiveSubTab('analytics')}
                                    className={`px-8 py-2.5 rounded-xl text-sm font-black transition-all flex items-center gap-1.5 ${activeSubTab === 'analytics'
                                            ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-900/20'
                                            : 'text-indigo-600 hover:bg-indigo-50 font-black'
                                        }`}
                                >
                                    <BarChart2 size={16} />
                                    성적 그래프
                                </button>
                            </div>

                            {activeSubTab === 'analytics' ? (
                                <SuccessAnalyticsChart stories={stories} isDarkMode={false} />
                            ) : (
                                <>
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-slate-100/70 p-3.5 rounded-2xl border border-slate-200">
                                        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                                            <span className="text-xs font-black text-slate-500 mr-1">필터:</span>
                                            
                                            <select
                                                value={selectedSubject}
                                                onChange={(e) => setSelectedSubject(e.target.value)}
                                                className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-xs font-bold shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                                            >
                                                <option value="전체">전체 과목</option>
                                                {SUBJECT_TYPES.map(sub => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>

                                            <select
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(e.target.value)}
                                                className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-xs font-bold shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                                            >
                                                <option value="전체">전체 연도</option>
                                                {yearList.map(yr => (
                                                    <option key={yr} value={yr}>{yr}년</option>
                                                ))}
                                            </select>

                                            <select
                                                value={selectedSemester}
                                                onChange={(e) => setSelectedSemester(e.target.value)}
                                                className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-xs font-bold shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                                            >
                                                <option value="전체">전체 학기/시험</option>
                                                {TERM_TYPES.map(term => (
                                                    <option key={term} value={term}>{term}</option>
                                                ))}
                                            </select>

                                            {(selectedYear !== '전체' || selectedSemester !== '전체' || selectedSubject !== '전체' || searchTerm) && (
                                                <button
                                                    onClick={() => { setSelectedYear('전체'); setSelectedSemester('전체'); setSelectedSubject('전체'); setSearchTerm(''); }}
                                                    className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline px-2 py-1"
                                                >
                                                    필터 초기화
                                                </button>
                                            )}
                                        </div>

                                        <div className="relative w-full md:w-64">
                                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                            <input
                                                className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-xs font-medium bg-white"
                                                placeholder="이름/학교 검색..."
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                                        {displayedStories.map((story) => (
                                            <div key={story.id} className="relative group">
                                                {/* Actions floating on top right */}
                                                <div className="absolute top-4 right-4 z-30 flex gap-1">
                                                    <button onClick={() => startEdit(story)} className="p-2 bg-slate-900/80 backdrop-blur rounded-xl text-slate-400 hover:text-indigo-400 transition-colors shadow-lg">
                                                        <Edit2 size={14} />
                                                    </button>
                                                    <button onClick={() => handleDelete(story.id!)} className="p-2 bg-slate-900/80 backdrop-blur rounded-xl text-slate-400 hover:text-rose-400 transition-colors shadow-lg">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                <LaurelSuccessCard story={story} />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Infinite Scroll Trigger */}
                                    {hasMore && (
                                        <div ref={observerTargetRef} className="flex justify-center items-center py-8">
                                            <div className="flex gap-2 items-center text-slate-400 text-xs font-bold">
                                                <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                <span className="ml-2">더 많은 실적 불러오는 중...</span>
                                            </div>
                                        </div>
                                    )}

                                    {filteredStories.length === 0 && !loading && (
                                        <div className="text-center py-20 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                                            <GraduationCap className="mx-auto text-slate-300 mb-3" size={48} />
                                            <p className="text-slate-500 font-medium">등록되거나 조건에 맞는 실적이 없습니다.</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SuccessRoot;
